import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { PiUsersThreeLight } from 'react-icons/pi';
import { showSidePanel } from '../../../store/actions/sidePanelActions';
import CollaboratorSelector, { Collaborator } from '../CollaboratorSelector';
import Button from '../TailwindComponents/Button';
import { FIELD_LABEL_CLASS, FIELD_HELP_CLASS, FIELD_GAP } from '../TailwindComponents/FieldShell';
import AccessList from './AccessList';
import { normaliseAll } from './accessModel';
import { useInSidePanel } from './inSidePanel';

export interface AccessFieldProps {
  /** Already-translated label. */
  label?: string;
  description?: string;
  value: Collaborator[];
  onChange: (collaborators: Collaborator[]) => void;
  /** Passed to the selector in the panel (ownerLabel, enableLeadSelection, …). */
  selectorProps?: Record<string, any>;
  disabled?: boolean;
}

/**
 * Access on a create form: who will have it, and one way to change it.
 *
 * Forms used to drop the whole people picker inline - a search box, a list and
 * per-row access dropdowns in the middle of a form. Everywhere else access is
 * a summary that opens the access side panel, so a create form now says the
 * same thing the record will say once it exists (feedback round 3, S8).
 *
 * Inside the side panel itself (Add task, Add contact…) there is no second
 * panel to open - the slot holds the form - so the selector opens in place.
 */
const AccessField: React.FC<AccessFieldProps> = ({
  label,
  description,
  value,
  onChange,
  selectorProps,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const people = value || [];
  const inPanel = useInSidePanel();
  const [inlineOpen, setInlineOpen] = useState(false);

  const openPanel = () =>
    dispatch(
      showSidePanel({
        type: 'draftAccess',
        showSidePanel: true,
        message: label || t('Access'),
        collaborators: people,
        onChange,
        selectorProps,
      })
    );

  return (
    <div data-testid="access-field">
      {label && <p className={`${FIELD_LABEL_CLASS} ${FIELD_GAP}`}>{label}</p>}
      {description && <p className={`${FIELD_HELP_CLASS} ${FIELD_GAP}`}>{description}</p>}
      {inPanel && inlineOpen ? (
        <CollaboratorSelector
          {...selectorProps}
          collaborators={people}
          onCollaboratorsChange={onChange}
        />
      ) : (
        <AccessList
          people={normaliseAll(people)}
          emptyText={t('Only you, until you share it.')}
        />
      )}
      <div className="mt-2">
        <Button
          type="button"
          variant="outline"
          size="small"
          icon={<PiUsersThreeLight className="h-4 w-4" />}
          onClick={inPanel ? () => setInlineOpen((o) => !o) : openPanel}
          disabled={disabled}
          aria-expanded={inPanel ? inlineOpen : undefined}
        >
          {inPanel && inlineOpen ? t('Done') : t('Manage access')}
        </Button>
      </div>
    </div>
  );
};

export default AccessField;
